<template>
  <main class="bg-white">
    <v-toolbar flat class="main-header main-header__shadow mb-6 px-5 py-2">
      <v-toolbar-title class="main-header__title">
        <h1>
          <v-icon class="mx-3 mb-1" @click="back">mdi-arrow-left</v-icon>
          <span v-if="!isEdited">{{ $t("new_subject") }}</span>
          <span v-else>{{ $t("edit_subject") }}</span>
        </h1>
      </v-toolbar-title>
    </v-toolbar>
    <v-container>
      <v-alert v-if="globalServerErrors !== null" dense outlined type="error">
        <p
          class="mb-0"
          v-for="globalErr in globalServerErrors"
          :key="globalErr"
        >
          {{ globalErr[0] }}
        </p>
      </v-alert>
      <formWrapper :validator="$v.form">
        <form @submit.prevent="handleSave" class="form-box">
          <v-row>
            <v-col cols="12" v-if="isEdited">
              <locale-select @change="fireLocaleChange" />
            </v-col>
            <v-col cols="12">
              <section class="transparent-field mt-5">
                <v-switch
                  v-model="form.visible"
                  inset
                  false-value="0"
                  true-value="1"
                  :label="$t('visible')"
                  color="success"
                ></v-switch>
              </section>
            </v-col>
            <v-col cols="12" class="align-self-center mb-4">
              <h4 class="mb-3">
                {{ $t("image") }}<sup style="color: red">*</sup>
              </h4>
              <section class="file-upload">
                <new-image-upload
                  class="file-upload__image"
                  :imgUrl="form.image ? form.image.path : ''"
                  :imgId="form.image ? form.image.id : ''"
                  @fileSelected="profileChange"
                  fieldName="mainImgFile"
                  :maxSize="maxSize"
                  :reset="reset"
                />
              </section>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <form-group name="name" :attribute="`fields.name`">
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.name"
                    type="text"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <date-time-picker
                v-model.trim="form.purge_date"
                :date-time="form.purge_date"
                :textFieldProps="{
                  regular: true,
                  label: $t('fields.purge_date'),
                  dense: true,
                }"
                timeFormat="HH:mm:ss"
              ></date-time-picker>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <form-group
                name="degree_level"
                :attribute="`fields.degree_level`"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-autocomplete
                    :items="degree_levels"
                    item-text="name"
                    item-value="id"
                    :loading="degreeLoading"
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.degree_level"
                    type="text"
                    dense
                  >
                  </v-autocomplete>
                </template>
              </form-group>
            </v-col>

            <v-col class="py-0" cols="12" lg="6">
              <v-autocomplete
                :items="tags"
                item-text="name"
                item-value="id"
                multiple
                chips
                :loading="tagsLoading"
                outlined
                v-model.trim="form.tags"
                :label="$t('fields.tags')"
                dense
                type="text"
              >
              </v-autocomplete>
            </v-col>
            <v-col class="py-0" cols="12">
              <v-row>
                <v-col cols="11">
                  <form-group name="video_url" :attribute="`fields.video_url`">
                    <template slot-scope="{ attrs, listeners }">
                      <v-text-field
                        v-bind="attrs"
                        v-on="listeners"
                        outlined
                        v-model.trim="form.video_url"
                        type="text"
                        dense
                      >
                      </v-text-field>
                    </template>
                  </form-group>
                </v-col>
                <v-col cols="1">
                  <v-btn
                    @click.prevent="addUrls"
                    :disabled="
                      form.video_url == '' || $v.form.video_url.$invalid
                    "
                    block
                    large
                    class="primary"
                  >
                    <v-icon>mdi-plus</v-icon>
                    <span>{{ $t("button.add") }}</span>
                  </v-btn>
                </v-col>
                <v-col
                  cols="12"
                  v-for="(item, index) in form.promo_media"
                  :key="index"
                >
                  <v-row>
                    <v-col cols="11">
                      <v-text-field
                        dense
                        readonly
                        outlined
                        v-model="item.path"
                        type="text"
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="1" @click="deleteUrl(index, item)">
                      <v-btn block large class="error">
                        <v-icon>mdi-minus</v-icon>
                        <span>{{ $t("button.delete") }}</span>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="py-0" cols="12">
              <form-group name="description" :attribute="`fields.description`">
                <template slot-scope="{ attrs, listeners }">
                  <v-textarea
                    v-bind="attrs"
                    v-on="listeners"
                    no-resize
                    rows="4"
                    outlined
                    v-model.trim="form.description"
                    :label="$t('fields.description')"
                    type="text"
                  >
                  </v-textarea>
                </template>
              </form-group>
            </v-col>

            <v-col cols="12">
              <v-btn
                class="success text-capitalize"
                large
                type="submit"
                width="30%"
                :disabled="$v.form.$invalid || this.form.video_url !== ''"
                :loading="loading"
                ><span>{{ $t("button.save") }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </form>
      </formWrapper>
    </v-container>
  </main>
</template>

<script>
import { required, minLength, maxLength, url } from "vuelidate/lib/validators";
import cookie from "js-cookie";

import {
  StoreData,
  ShowData,
  UpdateData,
  IndexData,
  DeleteMedia,
} from "@/helpers/apiMethods";

export default {
  name: "SubjectsCrud",

  components: {
    DateTimePicker: () => import("@/components/DateTimePicker"),
  },
  data() {
    return {
      globalServerErrors: null,
      isEdited: false,
      loadingData: false,
      loading: false,
      locale: cookie.get("language") || "en",
      reset: false,
      degree_levels: [],
      tagsLoading: true,
      degreeLoading: true,
      maxSize: 1.24,
      updatedPromoMedia: [],
      tags: [],
      form: {
        video_url: "",
        name: "",
        visible: 0,
        degree_level: "",
        description: "",
        purge_date: "",
        image: "",
        promo_media: [],
        tags: [],
      },
    };
  },

  created() {
    this.editCreateBoolean();
    this.getData();
  },
  methods: {
    addUrls() {
      this.form.promo_media.push({ path: this.form.video_url });
      this.updatedPromoMedia.push({ path: this.form.video_url });

      this.form.video_url = "";
      this.$v.form.video_url.$reset();
    },
    deleteUrl(index, item) {
      if (!item.id) {
        this.$delete(this.form.promo_media, index);
      } else {
        this.popUp().then((value) => {
          if (!value.dismiss) {
            DeleteMedia(item.id).then(() => {
              this.$delete(this.form.promo_media, index);
            });
          }
        });
      }
    },
    profileChange(photo) {
      this.form.image = photo.file;
      this.reset = false;
    },
    getData() {
      const fields = ["tags", "degree-levels"];
      const formFields = ["tags", "degree_levels"];
      const loadingFields = ["tagsLoading", "degreeLoading"];
      fields.forEach((field, index) => {
        IndexData({
          reqName: `admin-panel/${field}`,
          query: { pagination: "all" },
        }).then((res) => {
          const { data } = res.data;
          this[formFields[index]] = data;
          this[loadingFields[index]] = false;
        });
      });
    },
    editCreateBoolean() {
      if (this.$route.params.id) {
        this.isEdited = true;
      } else {
        this.isEdited = false;
      }
    },

    show(id, locale) {
      ShowData({
        reqName: `admin-panel/subjects`,
        id: id,
        locale,
      })
        .then((res) => {
          const { subject } = res.data;
          this.form = {
            name: subject.name,
            tags: subject.tags.map((tag) => tag.id),
            promo_media: subject.promo_media,
            degree_level: subject.degree_level.id,
            video_url: "",
            image: subject.image,
            description: subject.description,
            purge_date:
              subject.subscriptions_purge_date &&
              new Date(subject.subscriptions_purge_date),
          };
          if (subject.visible == true) {
            this.form.visible = "1";
          } else {
            this.form.visible = "0";
          }
        })
        .catch(() => {});
    },
    update(formData) {
      this.loading = true;

      UpdateData({
        reqName: `admin-panel/subjects`,
        data: formData,
        id: this.$route.params.id,
      })
        .then(() => {
          this.isEdited = false;
          this.loading = false;
          this.back();
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildData() {
      if (this.isEdited) {
        delete this.form.image;
      }
      const dateFormat = "YYYY-MM-DD HH:mm:ss";
      if (this.form.purge_date) {
        this.form.purge_date = this.$moment(this.form.purge_date).format(
          dateFormat
        );
      }
      const explodeField = ["tags", "promo_media"];
      const formData = new FormData();
      for (const key in this.form) {
        if (!explodeField.includes(key) && this.form[key] !== "") {
          formData.append(key, this.form[key]);
        }
      }
      !this.isEdited &&
        this.form.promo_media &&
        this.form.promo_media.forEach((media, index) => {
          formData.append(`promo_media[${index}]`, media.path);
        });
      this.isEdited &&
        this.updatedPromoMedia &&
        this.updatedPromoMedia.forEach((media, index) => {
          formData.append(`promo_media[${index}]`, media.path);
        });
      this.form.tags &&
        this.form.tags.forEach((el, index) => {
          formData.append(`tags[${index}]`, el);
        });
      formData.append("locale", this.locale);

      if (this.isEdited) {
        formData.append("_method", "put");
      }
      return formData;
    },
    handleSave() {
      if (!this.isEdited) {
        this.create(this.buildData());
      } else {
        this.update(this.buildData());
      }
    },

    create(formData) {
      this.loading = true;

      StoreData({
        reqName: `admin-panel/subjects`,
        data: formData,
      })
        .then(() => {
          this.loading = false;

          this.back();
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    back() {
      history.go(-1);
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    fireLocaleChange(locale) {
      this.show(this.$route.params.id, locale);
      this.locale = locale;
    },
  },
  watch: {
    $route: {
      handler({ params }) {
        if (params.id) {
          this.show(params.id);
        }
      },
      immediate: true,
    },
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(190),
      },
      degree_level: {
        required,
      },
      image: {
        required,
      },
      description: {
        minLength: minLength(3),
        maxLength: maxLength(10000),
      },
      video_url: {
        url,
      },
    },
  },
};
</script>
